import type { Dispatch } from 'redux';

import fetcher from 'api/fetcher';
import type { AxiosError } from 'axios';
import { matchPath } from 'react-router';
import type { Entries } from 'types';
import { resolvePromisesMap } from 'utils/clickme';
import type { CacheParams, FetchCacheController } from 'utils/fetchCache';

import { RProgressApi } from '@hh.ru/rprogress';

import routes, { Match, MethodsMap, NotFoundRoute, Route, ROUTES_REQUIREMENTS } from 'app/routes';
import { RouteType } from 'app/routes/routeType';

import type { Action } from 'store/createReducer';
import { extract, inject } from 'store/injector';
import type { BatchDispatch } from 'store/middlewares/batched';
import type { FetchAction, FetchUnloadedProps, NeedLoadMoreProps } from 'store/middlewares/fetchRouter/types';

const DEFAULT_CACHE_OPTIONS = { cacheable: true };

export const computeCacheOptions = (options: CacheParams): CacheParams =>
    typeof options.cacheable === 'boolean'
        ? options
        : {
              ...DEFAULT_CACHE_OPTIONS,
              ...options,
          };

export const cacheResponse = (
    requestSettings: Record<string, MethodsMap>,
    fetchData: Record<string, Record<string, unknown> | undefined>,
    fetchCacheController: FetchCacheController
): void => {
    Object.entries(fetchData).forEach(([requestAssignee, requestSetting]) => {
        const methods = requestSettings[requestAssignee];

        if (!methods || !requestSetting) return;

        Object.entries(methods).forEach(([methodName, methodProperties]) => {
            fetchCacheController.setCache(methodProperties.url, requestSetting[methodName]);
        });
    });
};

export const getRequestsMap = <T extends Record<string, MethodsMap>>(
    requestSettings: T,
    fetchCacheController: FetchCacheController
): Record<keyof T, Record<keyof T[keyof T], Promise<unknown>>> =>
    (Object.entries(requestSettings) as Entries<typeof requestSettings>).reduce(
        (result, [requestAssignee, requestSetting]) => {
            result[requestAssignee] = {} as Record<keyof T[keyof T], Promise<unknown>>;

            (Object.entries(requestSetting) as Entries<typeof requestSetting>).forEach(
                ([methodName, { url, method = 'get', options, ...cacheParams }]) => {
                    const cache = fetchCacheController.getCacheByKey(url, computeCacheOptions(cacheParams));

                    result[requestAssignee][methodName] = cache
                        ? Promise.resolve(cache)
                        : fetcher[method](url, options);
                }
            );

            return result;
        },
        {} as Record<keyof T, Record<keyof T[keyof T], Promise<unknown>>>
    );

export const needLoadMore = ({ additionalMethods, isDataReady, ...isDataReadyProps }: NeedLoadMoreProps): boolean =>
    Boolean(additionalMethods && isDataReady && !isDataReady(isDataReadyProps));

export const fetchUnloaded = ({
    additionalMethods,
    fetchCacheController,
    ...rest
}: FetchUnloadedProps): Promise<MethodsMap> => {
    const additionalMethodsResult = { additionalMethods: additionalMethods(rest) };

    rest.requestSettings.pageMethods = rest.requestSettings.pageMethods || {};
    Object.assign(rest.requestSettings.pageMethods, additionalMethodsResult.additionalMethods);

    return resolvePromisesMap(getRequestsMap(additionalMethodsResult, fetchCacheController)).then((responses) => {
        const currentResult = { ...rest.result, ...responses.additionalMethods } as MethodsMap;

        return needLoadMore({ ...rest, additionalMethods, result: currentResult })
            ? fetchUnloaded({ ...rest, additionalMethods, fetchCacheController, result: currentResult })
            : currentResult;
    });
};

export const isCanceledFetchingData = (action: FetchAction): boolean =>
    Boolean(action?.payload?.args?.[2]?.cancelFetchingData);

export const getRoute = (url: string): Route & Match => {
    for (const route of routes) {
        const { path, exact = true } = route;
        const match = matchPath(url, { path, exact });
        if (match) {
            return { ...route, match };
        }
    }
    const matchNotFound = {
        url: NotFoundRoute.path,
        path: NotFoundRoute.path,
        isExact: false,
        params: {},
    };
    return { ...NotFoundRoute, match: matchNotFound };
};

export const injectData = (
    result: Action[],
    urlWasChanged: boolean,
    action: Action,
    next: BatchDispatch<Action>
): void => {
    try {
        inject(result);
        next(urlWasChanged ? extract(action) : action);
    } catch (error) {
        throw error;
    }
};

export const onError = (
    type: RouteType,
    urlWasChanged: boolean,
    action: Action,
    next: Dispatch,
    error: AxiosError
): void => {
    RProgressApi.complete();
    const errorMethod = ROUTES_REQUIREMENTS[type].error;
    const actions = errorMethod ? errorMethod(error) : [];
    injectData(actions, urlWasChanged, action, next);
};
